import { useState } from "react";
import { X } from "lucide-react";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import TradingViewChart from "./TradingViewChart";

/**
 * Graphique TradingView d'un actif (bouton « Voir plus » des cartes de l'accueil).
 * Sur téléphone (sous 640 px) : plein écran, avec un bouton Fermer bien visible en haut,
 * et un graphique à la hauteur de l'écran. Sur ordinateur : fenêtre centrée, comme avant.
 */
const isPhone = () => typeof window !== "undefined" && window.innerWidth < 640;

// Hauteur du graphique, calculée à l'ouverture : tout l'écran sous l'en-tête sur téléphone,
// 640 px sur ordinateur (moins si la fenêtre du navigateur est basse, pour garder la fenêtre entière visible)
function chartHeight() {
  if (typeof window === "undefined") return 640;
  if (isPhone()) return Math.max(320, window.innerHeight - 68);
  return Math.max(360, Math.min(640, window.innerHeight - 150));
}

function ChartBody({ card }) {
  const [height] = useState(chartHeight);
  return (
    <div className="p-3 max-sm:p-0">
      <TradingViewChart
        symbol={card.tv_symbol}
        height={height}
        hideVolume
        studies={[
          { id: "MASimple@tv-basicstudies", inputs: { length: 50 } },
          { id: "MASimple@tv-basicstudies", inputs: { length: 200 } },
        ]}
      />
    </div>
  );
}

export default function AssetChartDialog({ card, open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-6xl w-[95vw] bg-[#0A0A0A] border-white/10 p-0 gap-0 overflow-hidden max-sm:inset-0 max-sm:w-full max-sm:max-w-none max-sm:h-[100dvh] max-sm:translate-x-0 max-sm:translate-y-0 max-sm:border-0 max-sm:content-start max-sm:data-[state=open]:slide-in-from-left-0 max-sm:data-[state=open]:slide-in-from-top-0 max-sm:data-[state=open]:zoom-in-100 max-sm:[&>button:last-child]:hidden"
        style={{ paddingTop: isPhone() ? "env(safe-area-inset-top)" : undefined }}
        data-testid="asset-chart-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/5 max-sm:flex-row max-sm:items-center max-sm:gap-3 max-sm:space-y-0 max-sm:px-4 max-sm:py-2.5 max-sm:text-left">
          <DialogTitle className="font-display font-black text-xl max-sm:text-base max-sm:min-w-0 max-sm:flex-1 max-sm:truncate">
            {card ? (
              <>
                <span className="text-emerald-400">{card.key}</span>{" "}
                <span className="text-zinc-400 font-normal">— {card.name}</span>
              </>
            ) : (
              "Graphique"
            )}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500 max-sm:sr-only">
            Graphique interactif TradingView. Zoomez, ajoutez des indicateurs et changez d'intervalle.
          </DialogDescription>
          {/* Téléphone : bouton Fermer bien visible (la petite croix du coin est masquée) */}
          <DialogClose
            className="sm:hidden shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/10 bg-white/5 text-sm text-zinc-200 outline-none active:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/25"
            data-testid="asset-chart-close"
          >
            <X className="w-4 h-4" /> Fermer
          </DialogClose>
        </DialogHeader>
        {open && card && <ChartBody card={card} />}
      </DialogContent>
    </Dialog>
  );
}
